import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import { useHandshakeCountdown } from '@repo/hooks'
import { QIcon_clock, QIcon_close_default } from '@repo/icons/index'
import React, { useEffect } from 'react'
import { HandshakePopupInfo } from '../../../../modules/apis/student/tutors/handshake/types'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import { n_classroom, s_common } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { AVATAR_IMAGE } from '../../../../modules/svg/custom'

interface IHandshakeToStudentPopup {
  showPopup: boolean
  setShowPopup: React.Dispatch<React.SetStateAction<boolean>>
  handshakeInfo: HandshakePopupInfo
  onReservationClick: () => void
}

const HandshakeToStudentPopup: React.FC<IHandshakeToStudentPopup> = ({
  showPopup,
  setShowPopup,
  handshakeInfo,
  onReservationClick,
}) => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()

  const { tutorId, tutorName, tutorImageUrl, scheduleDate, expiredTime } = handshakeInfo

  // Use handshake countdown hook
  const { timeLeft, isExpired } = useHandshakeCountdown(expiredTime, {
    format: 'MM:SS',
    timezone: currentUser?.timezone || 'UTC',
  })

  // Auto close popup when countdown expires
  useEffect(() => {
    if (isExpired) {
      setShowPopup(false)
    }
  }, [isExpired, setShowPopup])

  const handleReservationClick = () => {
    if (isMobile || isWebview) {
      setShowPopup(false)
    } else {
      onReservationClick()
      setShowPopup(false)
    }
  }

  if (!showPopup) return null

  const isBottomSheet = isMobile || isWebview

  const title = t(n_classroom.private_slot.title)
  const subtitle = t(n_classroom.private_slot.subtitle(currentUser?.profile?.name))
  const subtitle2 = t(n_classroom.private_slot.subtitle_2)
  const timezone_str = t(n_classroom.private_slot.your_timezone(currentUser?.timezone))
  const reservationButtonText = isMobile || isWebview ? t(s_common.ok) : t(n_classroom.private_slot.btn_book)

  return (
    <>
      {/* Backdrop for bottom sheet */}
      {isBottomSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[999]" onClick={() => setShowPopup(false)} />
      )}

      <div
        className={`fixed z-[1000] bg-white p-[24px] ${
          isBottomSheet
            ? 'bottom-0 left-0 right-0 rounded-t-[16px] shadow-[0px_-2px_4px_0px_rgba(0,0,0,0.2)]'
            : 'bottom-[24px] right-[24px] w-[400px] rounded-[16px] shadow-[0px_-2px_20px_0px_rgba(0,0,0,0.1)]'
        }`}
      >
        {/* Close button */}
        {!isBottomSheet && (
          <button
            onClick={() => setShowPopup(false)}
            className="absolute right-[24px] top-[24px] w-[24px] h-[24px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
          >
            <QIcon_close_default width={16} height={16} color={COLORS.GRAY600} />
          </button>
        )}

        {/* Title */}
        <div className="mb-[8px]">
          <h2 className="Headline2-24Bd text-black">{title}</h2>
        </div>

        {/* Subtitle */}
        <div className="mb-[32px]">
          <p className="Body1-16Md text-gray-500">{subtitle}</p>
          {isBottomSheet && <p className="Body1-16Md text-gray-500">{subtitle2}</p>}
        </div>

        {/* Countdown timer */}
        {!isBottomSheet && (
          <div className="mb-[16px] inline-flex items-center gap-[4px] px-[10px] py-[4px] bg-blue-100 rounded-[4px]">
            <QIcon_clock width={16} height={16} color={COLORS.BLUE500} />
            <span className="Body2-14Bd text-blue-500">{timeLeft}</span>
          </div>
        )}

        {/* Tutor info card */}
        <div className="mb-[32px] p-[16px] border border-gray-200 rounded-[8px]">
          {/* Tutor profile */}
          <div className="flex items-center gap-[8px] mb-[16px]">
            <div className="relative w-[32px] h-[32px]">
              {tutorImageUrl ? (
                <img
                  src={tutorImageUrl}
                  alt="tutor avatar"
                  className="w-[32px] h-[32px] rounded-full bg-gray-100 object-cover"
                  onError={(e) => {
                    e.currentTarget.src = AVATAR_IMAGE
                  }}
                />
              ) : (
                <>
                  <img src={AVATAR_IMAGE} alt="tutor avatar" className="w-[32px] h-[32px] rounded-full bg-gray-100" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="Caption-12Bd text-gray-400">사진</span>
                  </div>
                </>
              )}
            </div>
            <span className="Body2-14Md text-gray-600">{tutorName}</span>
          </div>

          {/* Schedule info */}
          <div className="flex flex-col">
            <div className="Title1-20Md text-black mb-[4px]">{scheduleDate}</div>
            <div className="Caption-12Md text-gray-500">{timezone_str}</div>
          </div>
        </div>

        {/* Reservation button */}
        <DSButton
          type={BUTTON_TYPE.SOLID}
          size={BUTTON_SIZE.LG}
          customBgColor={COLORS.PURPLE600}
          onClick={handleReservationClick}
          className="w-full"
        >
          <DSButton.Label>{reservationButtonText}</DSButton.Label>
        </DSButton>
      </div>
    </>
  )
}

export default HandshakeToStudentPopup
